import { FileTextOutlined, InboxOutlined } from "@ant-design/icons";
import { message, Upload } from "antd";
import { Button as ButtonFlowBite, ButtonGroup } from "flowbite-react";
import { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../api";
import { PageHeading } from "../../externalComponents";
import MultiTagAutocomplete from "../../externalComponents/flow_editor/shared/panel/components/MultiTagAutocomplete";
import TagDisplay from "../../externalComponents/flow_editor/shared/panel/components/TagDisplay";
import { Button } from "../../externalComponents/tailwind/button";
import { Field, Label } from "../../externalComponents/tailwind/fieldset";
import { Input } from "../../externalComponents/tailwind/input";
import { cleanPhone } from "../../functions/cleanPhone";
import { CustomerContext } from "../../providers";

const { Dragger } = Upload;

const splitCsvIntoChunks = async (file, chunkSize = 50000) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split(/\r\n|\n/);
      const header = lines[0];
      const dataLines = lines.slice(1).filter((line) => line.trim());

      if (dataLines.length <= chunkSize) {
        resolve([file]);
        return;
      }

      const chunks = [];
      for (let i = 0; i < dataLines.length; i += chunkSize) {
        const chunkLines = dataLines.slice(i, i + chunkSize);
        const chunkContent = [header, ...chunkLines].join("\n");
        const chunkBlob = new Blob([chunkContent], { type: "text/csv" });
        const chunkFile = new File(
          [chunkBlob],
          `${file.name}-${i + 1}-of-${Math.ceil(dataLines.length / chunkSize)}`,
          { type: "text/csv" },
        );
        chunks.push(chunkFile);
      }

      resolve(chunks);
    };

    reader.onerror = () => {
      reject(new Error("Failed to read file"));
    };

    reader.readAsText(file);
  });
};

const ContactsImportPage = () => {
  const { user } = useContext(CustomerContext);
  const navigate = useNavigate();

  const [isUploading, setIsUploading] = useState(false);
  const [isUploaded, setIsUploaded] = useState(false);

  const [file, setFile] = useState(null);
  const [shouldOptinContacts, setShouldOptinContacts] = useState(null);
  const [autoTagName, setAutoTagName] = useState(
    `CSV Import - ${new Date().toISOString().replace("T", " ").slice(0, 19)} UTC`,
  );
  const [additionalTags, setAdditionalTags] = useState([]);

  const buttonClassList =
    "flex-1 border-[1px] border-gray-300 bg-transparent text-gray-800 hover:bg-gray-200 hover:text-gray-900 transition duration-200";

  return (
    <>
      <PageHeading title="Import contacts" />
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            width: "40vw",
            height: "25vh",
          }}
        >
          <br />
          <Link
            to="https://help.getsimio.com/optin-general/importer-des-contacts-via-csv-dans-simio"
            target="_blank"
            className="text-pink hover:underline"
          >
            How to import contacts with our .csv template?
          </Link>
          <Field className={"mt-4"}>
            <Label>CSV file</Label>

            <Dragger
              name="file"
              showUploadList={false}
              maxCount={1}
              accept="text/csv"
              disabled={isUploading}
              multiple={false}
              action={async (file) => {
                setFile(file);
              }}
              beforeUpload={(file) => {
                return new Promise((resolve, reject) => {
                  const isCSV =
                    file.type === "application/vnd.ms-excel" ||
                    file.type === "text/csv";

                  if (!isCSV) {
                    message.error("You can only import CSV files");
                    return reject();
                  }

                  const reader = new FileReader();
                  reader.onload = (e) => {
                    const text = e.target.result;
                    const lines = text
                      .split(/\r\n|\n/)
                      .filter((line) => line.trim());

                    if (lines.length > 160000) {
                      message.error("File has more than 160,000 rows");
                      return reject();
                    }

                    if (lines.length === 0) {
                      message.error("File is empty");
                      return reject();
                    }
                    const hasDelimiter =
                      lines[0].includes("|") ||
                      lines[0].includes(";") ||
                      lines[0].includes(",");
                    const delimiter = hasDelimiter
                      ? [
                          lines[0].includes(";")
                            ? ";"
                            : lines[0].includes(",")
                              ? ","
                              : "|",
                        ]
                      : [",", ";"];

                    const headers = lines[0]
                      .split(delimiter)
                      .map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));

                    if (headers[0] !== "phonenumber") {
                      message.error("First column must be 'phoneNumber'");
                      return reject();
                    }

                    const phoneNumbers = new Set();
                    const duplicates = [];

                    for (let i = 1; i < lines.length; i++) {
                      const line = lines[i];
                      if (!line.trim()) continue;

                      const columns = line.split(delimiter);
                      const rawPhone = columns[0]?.trim().replace(/['"]/g, "");
                      const cleanedPhone = cleanPhone(rawPhone);

                      if (cleanedPhone) {
                        if (phoneNumbers.has(cleanedPhone)) {
                          duplicates.push(rawPhone);
                        } else {
                          phoneNumbers.add(cleanedPhone);
                        }
                      }
                    }

                    const uniqueDuplicates = Array.from(new Set(duplicates));
                    if (uniqueDuplicates.length > 0) {
                      const firstThree = uniqueDuplicates
                        .slice(0, 3)
                        .join(", ");
                      message.error(
                        `File contains ${duplicates.length} duplicate phone number(s) to remove. Examples: ${firstThree}${uniqueDuplicates.length > 3 ? ", ..." : ""}`,
                      );
                      return reject();
                    }

                    resolve(file);
                  };

                  reader.onerror = () => {
                    message.error("Failed to read file");
                    reject();
                  };

                  reader.readAsText(file);
                });
              }}
              onDrop={() => {}}
            >
              <p className="ant-upload-drag-icon">
                {file ? <FileTextOutlined /> : <InboxOutlined />}
              </p>
              <p className="ant-upload-text">
                Click or drag-and-drop a file to import it
              </p>
              {file ? (
                <p className="ant-upload-hint">{file.name}</p>
              ) : (
                <p className="ant-upload-hint">
                  Please upload a .csv file separated by commas or semicolons,
                  with less than 160k rows.
                </p>
              )}
            </Dragger>
          </Field>

          <Field className="mt-4 flex flex-col">
            <Label>New contacts opt-in status</Label>
            <ButtonGroup className="w-full">
              <ButtonFlowBite
                onClick={() => setShouldOptinContacts(true)}
                type="button"
                color="alternative"
                className={
                  buttonClassList +
                  (shouldOptinContacts && " bg-gray-200 text-gray-900")
                }
              >
                Opted in
              </ButtonFlowBite>
              <ButtonFlowBite
                onClick={() => setShouldOptinContacts(false)}
                type="button"
                color="alternative"
                className={
                  buttonClassList +
                  (shouldOptinContacts === false &&
                    " bg-gray-200 text-gray-900")
                }
              >
                Not opted in
              </ButtonFlowBite>
            </ButtonGroup>
          </Field>

          <Field className={"mt-4"}>
            <Label>Auto Tag</Label>
            <Input disabled value={autoTagName} />
          </Field>

          <Field className={"mt-4"}>
            <Label>Additional Tags (Optional)</Label>
            <MultiTagAutocomplete
              selectedTags={additionalTags}
              onTagsChange={setAdditionalTags}
              customerId={user.customerId}
            />
            <TagDisplay
              tags={additionalTags}
              onRemoveTag={(tagId) => {
                setAdditionalTags(
                  additionalTags.filter((tag) => tag.id !== tagId),
                );
              }}
            />
          </Field>

          <Button
            color="primaryButton"
            className="flex w-full justify-center bg-yellow font-title font-bold leading-6 mt-4"
            disabled={
              !file || isUploading || isUploaded || shouldOptinContacts === null
            }
            loading={isUploading}
            onClick={async () => {
              try {
                setIsUploading(true);
                const newAutoTagName = `CSV Import - ${new Date().toISOString().replace("T", " ").slice(0, 19)} UTC`;
                setAutoTagName(newAutoTagName);

                const chunks = await splitCsvIntoChunks(file, 50000);
                const totalChunks = chunks.length;

                for (let i = 0; i < totalChunks; i++) {
                  const chunkFile = chunks[i];
                  const tagName =
                    totalChunks > 1
                      ? `${newAutoTagName}-${i + 1}-of-${totalChunks}`
                      : newAutoTagName;

                  const autoTag = await api.tag.create({
                    customer: { _link: user.customerId },
                    name: tagName,
                  });

                  await api.csvImport.create({
                    customer: { _link: user.customerId },
                    csv: {
                      file: chunkFile,
                    },
                    status: "pending",
                    objectType: "contact",
                    tag: { _link: autoTag.id },
                    shouldOptinContacts,
                    additionalTags:
                      additionalTags.length > 0
                        ? additionalTags.map((tag) => tag.id)
                        : null,
                  });
                }

                const successMessage =
                  totalChunks > 1
                    ? `${file.name} successfully split into ${totalChunks} imports. Treatment in progress.`
                    : additionalTags.length > 0
                      ? `${file.name} successfully imported with ${additionalTags.length + 1} tags. Treatment in progress.`
                      : `${file.name} successfully imported. Treatment in progress.`;

                message.success(successMessage);
                setIsUploading(false);
                setIsUploaded(true);
                navigate("/settings/data/imports");
              } catch (error) {
                message.error(`${file.name} could not be imported`);
                console.log(error);
                setIsUploading(false);
              }
            }}
          >
            Import now
          </Button>
        </div>
      </div>
    </>
  );
};
export default ContactsImportPage;
